import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Mail,
  Bell,
  Clock,
  Star,
  Settings,
  Save,
  Loader2,
  CheckCircle2,
  Send,
  RefreshCw,
  Calendar,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminAppointmentNotifications - Admin panel for managing appointment email notifications
 */
const AdminAppointmentNotifications = ({ country = 'SL' }) => {
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [country]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, statsRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointment-notifications/settings/${country}`),
        axios.get(`${API_URL}/api/appointment-notifications/stats`),
        axios.get(`${API_URL}/api/appointment-notifications/logs?limit=20`),
      ]);
      
      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch notification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/appointment-notifications/settings`, {
        ...settings,
        country
      });
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const processReminders = async () => {
    try {
      setProcessing(true);
      const response = await axios.post(`${API_URL}/api/appointment-notifications/process-pending-reminders`);
      alert(`Processed: ${response.data.sent} reminders sent`);
      fetchData();
    } catch (error) {
      console.error('Failed to process reminders:', error);
      alert('Failed to process reminders');
    } finally {
      setProcessing(false);
    }
  };

  const processFeedback = async () => {
    try {
      setProcessing(true);
      const response = await axios.post(`${API_URL}/api/appointment-notifications/process-pending-feedback`);
      alert(`Processed: ${response.data.sent} feedback requests sent`);
      fetchData();
    } catch (error) {
      console.error('Failed to process feedback:', error);
      alert('Failed to process feedback requests');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-appointment-notifications">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.confirmations_sent || 0}</p>
                <p className="text-xs text-gray-500">Confirmations Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Bell className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.reminders_sent || 0}</p>
                <p className="text-xs text-gray-500">Reminders Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Star className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.feedback_requests_sent || 0}</p>
                <p className="text-xs text-gray-500">Feedback Requests</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.feedback_completion_rate || 0}%</p>
                <p className="text-xs text-gray-500">Feedback Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-1" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="automation">
            <RefreshCw className="h-4 w-4 mr-1" />
            Automation
          </TabsTrigger>
          <TabsTrigger value="logs">
            <Mail className="h-4 w-4 mr-1" />
            Logs
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Email Notification Settings</CardTitle>
              <CardDescription>Configure appointment email notifications for {country}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Confirmation Emails */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">Booking Confirmations</h3>
                      <p className="text-sm text-gray-500">Send email when appointment is booked</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings?.confirmation_enabled ?? true}
                    onCheckedChange={(v) => setSettings({ ...settings, confirmation_enabled: v })}
                  />
                </div>
                {settings?.confirmation_enabled && (
                  <div>
                    <Label>Email Subject</Label>
                    <Input
                      value={settings?.confirmation_subject || ''}
                      onChange={(e) => setSettings({ ...settings, confirmation_subject: e.target.value })}
                      placeholder="Your appointment is confirmed!"
                    />
                  </div>
                )}
              </div>

              {/* Reminder Emails */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Bell className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">Appointment Reminders</h3>
                      <p className="text-sm text-gray-500">Send reminder before appointment</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings?.reminder_enabled ?? true}
                    onCheckedChange={(v) => setSettings({ ...settings, reminder_enabled: v })}
                  />
                </div>
                {settings?.reminder_enabled && (
                  <div className="space-y-3">
                    <div>
                      <Label>Send Reminder</Label>
                      <Select
                        value={String(settings?.reminder_hours_before || 24)}
                        onValueChange={(v) => setSettings({ ...settings, reminder_hours_before: parseInt(v) })}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2">2 hours before</SelectItem>
                          <SelectItem value="4">4 hours before</SelectItem>
                          <SelectItem value="12">12 hours before</SelectItem>
                          <SelectItem value="24">24 hours before</SelectItem>
                          <SelectItem value="48">48 hours before</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Email Subject</Label>
                      <Input
                        value={settings?.reminder_subject || ''}
                        onChange={(e) => setSettings({ ...settings, reminder_subject: e.target.value })}
                        placeholder="Reminder: Your appointment is tomorrow"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Feedback Request Emails */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Star className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">Feedback Requests</h3>
                      <p className="text-sm text-gray-500">Request reviews after service completion</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings?.feedback_enabled ?? true}
                    onCheckedChange={(v) => setSettings({ ...settings, feedback_enabled: v })}
                  />
                </div>
                {settings?.feedback_enabled && (
                  <div className="space-y-3">
                    <div>
                      <Label>Send Feedback Request</Label>
                      <Select
                        value={String(settings?.feedback_delay_hours || 2)}
                        onValueChange={(v) => setSettings({ ...settings, feedback_delay_hours: parseInt(v) })}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 hour after</SelectItem>
                          <SelectItem value="2">2 hours after</SelectItem>
                          <SelectItem value="4">4 hours after</SelectItem>
                          <SelectItem value="24">24 hours after</SelectItem>
                          <SelectItem value="48">48 hours after</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Email Subject</Label>
                      <Input
                        value={settings?.feedback_subject || ''}
                        onChange={(e) => setSettings({ ...settings, feedback_subject: e.target.value })}
                        placeholder="How was your appointment? Leave a review"
                      />
                    </div>
                  </div>
                )}
              </div>

              <Button onClick={saveSettings} disabled={saving} className="w-full">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automation Tab */}
        <TabsContent value="automation">
          <Card>
            <CardHeader>
              <CardTitle>Automation Controls</CardTitle>
              <CardDescription>Manage automated email processing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Reminder Automation */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium flex items-center gap-2">
                      <Bell className="h-4 w-4 text-amber-600" />
                      Appointment Reminders
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Check for upcoming appointments and send reminders
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline">
                        Check every {settings?.reminder_check_interval_minutes || 30} minutes
                      </Badge>
                      <Badge className={settings?.reminder_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                        {settings?.reminder_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={processReminders}
                    disabled={processing || !settings?.reminder_enabled}
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Process Now
                  </Button>
                </div>
              </div>

              {/* Feedback Automation */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium flex items-center gap-2">
                      <Star className="h-4 w-4 text-blue-600" />
                      Feedback Requests
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Check for completed appointments and request feedback
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline">
                        Check every {settings?.feedback_check_interval_minutes || 60} minutes
                      </Badge>
                      <Badge className={settings?.feedback_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                        {settings?.feedback_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={processFeedback}
                    disabled={processing || !settings?.feedback_enabled}
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Process Now
                  </Button>
                </div>
              </div>

              {/* Scheduler Info */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Scheduler Status
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  The automated scheduler runs in the background and processes pending notifications at the configured intervals.
                  Use &quot;Process Now&quot; to manually trigger immediate processing.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Notification Logs</CardTitle>
                  <CardDescription>Recent email notifications sent</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No notification logs yet</p>
              ) : (
                <div className="space-y-2">
                  {logs.map((log, idx) => (
                    <div key={log.id || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          log.type === 'appointment_confirmation' ? 'bg-green-100' :
                          log.type === 'appointment_reminder' ? 'bg-amber-100' :
                          'bg-blue-100'
                        }`}>
                          {log.type === 'appointment_confirmation' ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : log.type === 'appointment_reminder' ? (
                            <Bell className="h-4 w-4 text-amber-600" />
                          ) : (
                            <Star className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {log.type === 'appointment_confirmation' ? 'Confirmation' :
                             log.type === 'appointment_reminder' ? 'Reminder' : 'Feedback Request'}
                          </p>
                          <p className="text-xs text-gray-500">{log.recipient}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                          {log.status}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(log.sent_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAppointmentNotifications;
